import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";

const ShoppingCartNFTModal = ({ isVisible, setIsVisible }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const primaryColor = '#000000'; // Black primary color

  useEffect(() => {
    if (isVisible) {
      setTimeout(() => setVisible(true), 10); // trigger animation
    } else {
      setVisible(false);
    }
  }, [isVisible]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
  
    if (isVisible) {
      window.addEventListener('keydown', handleKeyDown);
    }
  
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isVisible]);

  const handleClose = () => {
    // Set a cookie to remember that the user has seen this modal
    Cookies.set("nftModalSeen", "false", { expires: 7 });
    setVisible(false);
    setTimeout(() => {
      setIsVisible(false);
    }, 300);
  };

  const goToNFTMarketplace = () => {
    Cookies.set("nftModalSeen", "false", { expires: 7 });
    navigate("/shopping");
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.65)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: visible ? 1 : 0,
        transition: 'opacity 0.3s ease-in-out',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        style={{
          transform: visible ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
          backgroundColor: '#fff',
          borderRadius: '20px',
          padding: '75px 50px',
          maxWidth: '520px',
          width: '95%',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
        }}
      >
        {/* Decorative element - top ribbon */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '8px',
            background: `linear-gradient(90deg, #000000, #333333)`,
          }}
        />
        
        {/* Close button */}
        <div
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: '15px',
            right: '15px',
            width: '35px',
            height: '35px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            backgroundColor: '#f0f0f0',
          }}
        >
          <span
            style={{
              fontSize: '36px',
              lineHeight: 1,
              color: '#333',
              fontWeight: 'bold',
            }}
          >
            ×
          </span>
        </div>

        {/* NFT icon */}
        <div
          style={{
            width: '110px',
            height: '110px',
            margin: '0 auto 20px',
            backgroundColor: '#f0f0f0',
            borderRadius: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg
            width="64"
            height="64"
            viewBox="0 0 24 24"
            fill="none"
            style={{ color: primaryColor }}
          >
            <path
              d="M20 7L12 3L4 7M20 7V17L12 21M20 7L12 11M12 21L4 17V7M12 21V11M4 7L12 11"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <h2
          style={{
            fontSize: '26px',
            marginTop: '2rem',
            marginBottom: '16px',
            color: '#222',
            fontWeight: '700',
          }}
        >
          {t("Want to unlock more benefits?")}
        </h2>
        
        <p
          style={{
            fontSize: '17px',
            color: '#444',
            marginBottom: '30px',
            lineHeight: '1.6',
          }}
        >
          {t("Without owning NFTs, you will not be able to build your community or claim bonuses.")}
          <br /><br />
          <strong style={{ color: primaryColor }}>
            {t("Purchase X-PASS NFTs to unlock all features and maximize your bonuses!")}
          </strong>
        </p>
        
        {/* Features list */}
        <div style={{ marginBottom: '35px', textAlign: 'left' }}>
          {['Purchase GemX membership', 'Build your community', 'Claim bonuses'].map((feature, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '12px',
                fontSize: '16px',
                color: '#333',
              }}
            >
              <div
                style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  backgroundColor: '#f0f0f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '12px',
                  flexShrink: 0,
                }}
              >
                <span style={{ color: primaryColor, fontSize: '14px' }}>✓</span>
              </div>
              {t(feature)}
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={handleClose}
            style={{
              backgroundColor: '#f0f0f0',
              color: '#333',
              padding: '14px 28px',
              fontSize: '16px',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer',
              transition: 'all 0.3s',
              fontWeight: '600',
              width: '40%',
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#e0e0e0';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#f0f0f0';
            }}
          >
            {t("Close")}
          </button>

          <button
            onClick={goToNFTMarketplace}
            style={{
              backgroundColor: primaryColor,
              color: '#fff',
              padding: '14px 28px',
              fontSize: '16px',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer',
              transition: 'all 0.3s',
              fontWeight: '600',
              boxShadow: `0 4px 12px rgba(0, 0, 0, 0.15)`,
              width: '60%',
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#333';
              e.target.style.transform = 'translateY(-2px)';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = primaryColor;
              e.target.style.transform = 'translateY(0)';
            }}
          >
            {t("Start")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShoppingCartNFTModal;